import React from 'react';
import { KatButton } from '@amzn/katal-react';
import { AiAssistDiagnosticResponse } from '../services/Ai/AiAssistService';
import './PSTriageAssistModal.scss';

interface PSTriageAssistModalProps {
    isOpen: boolean;
    onClose: () => void;
    diagnosticCode: string;
    diagnosticDescription: string;
    sampleRefundIds: string[];
    count: number;
    job?: AiAssistDiagnosticResponse | null;
}
const ACTIVE_JOB_STATUSES = new Set(['QUEUED', 'SUBMITTED', 'RUNNING']);

const PSTriageAssistModal: React.FC<PSTriageAssistModalProps> = ({
    isOpen,
    onClose,
    diagnosticCode,
    diagnosticDescription,
    sampleRefundIds,
    count,
    job,
}) => {
    if (!isOpen) {
        return null;
    }

    return (
        <div className="pstriage-assist-modal__overlay" onClick={onClose}>
            <div className="pstriage-assist-modal" onClick={(event) => event.stopPropagation()}>
                <div className="pstriage-assist-modal__header">
                    <div>
                        <div className="pstriage-assist-modal__eyebrow">PSTriage Job</div>
                        <h3>{diagnosticCode}</h3>
                        <p>{diagnosticDescription || 'Sample-based bucket diagnosis'}</p>
                    </div>
                    <button
                        type="button"
                        className="pstriage-assist-modal__close"
                        onClick={onClose}
                        aria-label="Close PSTriage assist"
                    >
                        x
                    </button>
                </div>

                <div className="pstriage-assist-modal__meta">
                    <span>Bucket count: {count}</span>
                    <span>Samples: {sampleRefundIds.length}</span>
                    {job?.status && <span>Status: {prettyStatus(job.status)}</span>}
                </div>

                <div className="pstriage-assist-modal__samples">
                    <strong>Sample refund IDs</strong>
                    <div>{sampleRefundIds.join(', ')}</div>
                </div>

                <div className="pstriage-assist-modal__body">
                    {job ? (
                        <div className="pstriage-assist-modal__response">
                            <div className="pstriage-assist-modal__response-meta">
                                {job.runtimeName && <span>{job.runtimeName}</span>}
                                {job.generatedAt && <span>{job.generatedAt}</span>}
                            </div>
                            {(job.sessionId || job.jobLink || job.executionLink) && (
                                <div className="pstriage-assist-modal__tracking">
                                    <div className="pstriage-assist-modal__tracking-title">PSTriage Tracking</div>
                                    {job.sessionId && (
                                        <div className="pstriage-assist-modal__tracking-row">
                                            <span>Session ID</span>
                                            <code>{job.sessionId}</code>
                                        </div>
                                    )}
                                    <div className="pstriage-assist-modal__tracking-links">
                                        {job.executionLink && (
                                            <a href={job.executionLink} target="_blank" rel="noopener noreferrer">
                                                Open ORCA workflow
                                            </a>
                                        )}
                                        {job.jobLink && (
                                            <a href={job.jobLink} target="_blank" rel="noopener noreferrer">
                                                Open ORCA search
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                            {job.answer && (
                                <div
                                    className="pstriage-assist-modal__response-text"
                                    dangerouslySetInnerHTML={{ __html: formatMarkdown(job.answer) }}
                                />
                            )}
                            {!job.answer && !job.errorMessage && (
                                <div className="pstriage-assist-modal__response-text">
                                    {job.status && ACTIVE_JOB_STATUSES.has(job.status)
                                        ? 'PSTriage is still working on this bucket. Sherlock will refresh the stored job status during the next diagnostics run.'
                                        : 'No PSTriage result summary is available for this job yet. Use the session tracking details above to inspect the Orca workflow directly.'}
                                </div>
                            )}
                            {job.errorMessage && (
                                <div className="pstriage-assist-modal__error">
                                    <p>{job.errorMessage}</p>
                                </div>
                            )}
                            {job.caveats && job.caveats.length > 0 && (
                                <div className="pstriage-assist-modal__caveats">
                                    {job.caveats.map((caveat) => (
                                        <div key={caveat}>{caveat}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="pstriage-assist-modal__response">
                            <div className="pstriage-assist-modal__response-text">
                                Not available yet.
                            </div>
                        </div>
                    )}
                </div>

                <div className="pstriage-assist-modal__footer">
                    <KatButton variant="secondary" label="Close" onClick={onClose} />
                </div>
            </div>
        </div>
    );
};

function prettyStatus(status: string): string {
    return status.charAt(0) + status.slice(1).toLowerCase();
}

function formatMarkdown(value: string): string {
    let html = value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');
    html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');
    html = html.replace(/\n\n/g, '</p><p>');

    return `<p>${html}</p>`;
}

export default PSTriageAssistModal;
